<template>
  <AuthLayout data-testid="set-password" :loading="loading" :disabled="loading">
    <AuthLoginLogo />

    <div v-if="stage === 'load'" class="set-password__loading">
      <loader />
    </div>

    <form
      v-else-if="stage === 'set'"
      class="set-password__form"
      data-testid="set-password__form"
      novalidate
      @submit.prevent="setPassword"
    >
      <header class="set-password__header">
        <h1 class="set-password__title" data-testid="set-password__title">
          {{ copy.SET_PASSWORD_HEADING }}
        </h1>
        <p class="set-password__subtitle" data-testid="set-password__subtitle">
          {{ chooseSubtitle }}
        </p>
      </header>

      <AuthLoginAlert
        :message="error"
        test-id="set-password__alert"
      />

      <div class="set-password__fields">
        <div class="set-password__field">
          <label class="set-password__label" for="set-password-new">
            {{ copy.SET_PASSWORD_NEW_LABEL }}
          </label>
          <input
            id="set-password-new"
            v-model="form.password"
            class="set-password__input text-transform-none"
            type="password"
            name="new-password"
            autocomplete="new-password"
            :disabled="loading"
            data-testid="set-password__new-password"
          />
          <p class="set-password__hint" data-testid="set-password__hint">
            {{ copy.SET_PASSWORD_POLICY }}
          </p>
        </div>

        <div class="set-password__field">
          <label class="set-password__label" for="set-password-confirm">
            {{ copy.SET_PASSWORD_CONFIRM_LABEL }}
          </label>
          <input
            id="set-password-confirm"
            v-model="form.repassword"
            class="set-password__input text-transform-none"
            :class="{ 'set-password__input--error': showMismatch }"
            type="password"
            name="confirm-password"
            autocomplete="new-password"
            :aria-invalid="showMismatch"
            :aria-describedby="showMismatch ? 'set-password-mismatch' : null"
            :disabled="loading"
            data-testid="set-password__confirm-password"
          />
          <p
            v-if="showMismatch"
            id="set-password-mismatch"
            class="set-password__field-error"
            role="alert"
            data-testid="set-password__mismatch"
          >
            {{ copy.SET_PASSWORD_MISMATCH }}
          </p>
        </div>

        <button
          class="set-password__primary"
          type="submit"
          :disabled="!canSubmit"
          data-testid="set-password__submit"
        >
          {{ copy.SET_PASSWORD_CREATE_SUBMIT }}
        </button>
        <button
          class="set-password__text"
          type="button"
          data-testid="set-password__cancel"
          @click="goHome"
        >
          {{ copy.SET_PASSWORD_CANCEL }}
        </button>
      </div>
    </form>

    <div
      v-else-if="stage === 'done'"
      class="set-password__success"
      data-testid="set-password__success"
    >
      <h1
        class="set-password__title"
        data-testid="set-password__success-title"
      >
        {{ copy.SET_PASSWORD_ALL_SET_HEADING }}
      </h1>
      <p
        class="set-password__success-body"
        data-testid="set-password__success-body"
      >
        {{ copy.SET_PASSWORD_SUCCESS_BODY }}
      </p>
      <p
        v-if="error"
        class="set-password__success-error"
        data-testid="set-password__success-error"
      >
        {{ error }}
      </p>
      <div class="set-password__actions">
        <button
          class="set-password__primary"
          type="button"
          data-testid="set-password__home"
          @click="goHome"
        >
          {{ copy.SET_PASSWORD_CONTINUE }}
        </button>
      </div>
    </div>

    <div v-else class="set-password__fallback">
      <noaccess class="pb-3" />
    </div>
  </AuthLayout>
</template>

<script>
import loader from '@/views/common/Loader';
import noaccess from '@/views/common/NoAccess';
import constants from '../../plugins/constants';
import { passwordMeetsPolicy } from './utils/authLoginUtils';
import AuthLayout from './components/AuthLayout.vue';
import AuthLoginAlert from './components/AuthLoginAlert.vue';
import AuthLoginLogo from './components/AuthLoginLogo.vue';

const { AUTH } = constants;

export default {
  name: 'SetPassword',
  components: {
    loader,
    noaccess,
    AuthLayout,
    AuthLoginAlert,
    AuthLoginLogo,
  },
  props: {
    props: { type: Object, default: () => ({}) },
  },
  data: () => ({
    stage: 'load',
    loading: false,
    email: '',
    form: {
      password: '',
      repassword: '',
      code: '',
    },
    error: '',
    copy: AUTH,
  }),
  computed: {
    passwordsMatch() {
      return Boolean(this.form.password) && this.form.password === this.form.repassword;
    },
    showMismatch() {
      return Boolean(this.form.password && this.form.repassword && !this.passwordsMatch);
    },
    policyMatch() {
      return passwordMeetsPolicy(this.form.password);
    },
    canSubmit() {
      return Boolean(
        this.form.password &&
          this.form.repassword &&
          this.policyMatch &&
          this.passwordsMatch &&
          !this.loading,
      );
    },
    chooseSubtitle() {
      if (this.email) {
        return this.copy.SET_PASSWORD_CHOOSE_SUBTITLE.replace('{email}', this.email);
      }
      return this.copy.SET_PASSWORD_CHOOSE_SUBTITLE_FALLBACK;
    },
  },
  watch: {
    'form.password'() {
      this.error = '';
    },
    'form.repassword'() {
      this.error = '';
    },
  },
  created() {
    this.form.code = String(this.$route.query.c || '').trim();
    this.email = String(this.$route.query.u || this.$route.query.email || '').trim();
    this.checkSetPassword();
  },
  beforeDestroy() {
    this.stage = 'load';
    this.loading = false;
    this.email = '';
    this.form = {
      password: '',
      repassword: '',
      code: '',
    };
    this.error = '';
  },
  methods: {
    goHome() {
      this.$router.push('/');
    },
    async checkSetPassword() {
      this.error = '';
      if (!this.form.code) {
        this.stage = '';
        return;
      }
      this.stage = 'load';
      this.loading = true;
      console.log('[SetPassword]:', { action: 'checkSetPassword' });
      try {
        const result = await this.$store.dispatch('auth/checkSetPassword', this.form.code);
        if (result?.success) {
          this.stage = 'set';
          return;
        }
        this.stage = '';
        this.error = result?.message || this.copy.RESET_GENERIC_ERROR;
      } catch (error) {
        this.stage = '';
        this.error = error?.message || this.copy.RESET_GENERIC_ERROR;
      } finally {
        this.loading = false;
      }
    },
    async setPassword() {
      if (!this.canSubmit) return;
      this.error = '';
      this.loading = true;
      console.log('[SetPassword]:', { action: 'setPassword' });
      try {
        const result = await this.$store.dispatch('auth/setPassword', this.form);
        if (result?.success) {
          this.stage = 'done';
          return;
        }
        this.error = result?.message || this.copy.RESET_GENERIC_ERROR;
      } catch (error) {
        this.error = error?.message || this.copy.RESET_GENERIC_ERROR;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './SetPassword.scss';
</style>
